<template>
  <div class="pie-container">
    <!-- 左侧：设备状态分布 -->
    <div class="status-distribution">
      <div class="header">
        <!-- <span class="indicator"></span> -->
        <h3 class="title-text">设备状态分布</h3>
      </div>
      
      <div class="chart-wrapper">
        <!-- 环形图容器 -->
        <div class="chart-box-outer">
          <div ref="donutRef" class="chart-box"></div>
          <!-- 环内中心文字 -->
          <div class="center-text">
            <span class="center-num">{{ totalCount }}</span>
            <span class="center-label">总数</span>
          </div>
        </div>
        
        <!-- 图例列表 -->
        <div class="legend-list">
          <div class="legend-item">
            <div class="legend-left">
              <span class="dot running"></span>
              <span class="label">运行中</span>
            </div>
            <span class="value">{{ runningCount }} 台</span>
          </div>
          <div class="legend-item">
            <div class="legend-left">
              <span class="dot shutdown"></span>
              <span class="label">已关机</span>
            </div>
            <span class="value">{{ shutdownCount }} 台</span>
          </div>
          <div class="legend-item">
            <div class="legend-left">
              <span class="dot faulty"></span>
              <span class="label">故障中</span>
            </div>
            <span class="value">{{ faultyCount }} 台</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：项目作业效率Top5 -->
    <div class="project-ranking">
      <div class="header">
        <!-- <span class="indicator"></span> -->
        <h3 class="title-text">项目作业效率Top5</h3>
      </div>

      <div class="ranking-list">
        <div class="ranking-item" v-for="(item, index) in rankingData" :key="index">
          <!-- 左侧：排名及工程名 -->
          <div class="item-left">
            <span :class="['rank-badge', `rank-${index + 1}`]">{{ index + 1 }}</span>
            <span class="project-name">{{ item.name }}</span>
          </div>
          <!-- 右侧：进度槽与比例值 -->
          <div class="item-right">
            <div class="progress-bar-bg">
              <div class="progress-bar-fill" :style="{ width: item.rate + '%', backgroundColor: item.color }"></div>
            </div>
            <span class="rate-text" :style="{ color: item.color }">{{ item.rate }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import * as echarts from "echarts";
import { EquipmentLedgerHomeStats } from "@/api/equipmentLedger.js";

const donutRef = ref(null);
let myChart = null;

const totalCount = ref(0);
const runningCount = ref(0);
const shutdownCount = ref(0);
const faultyCount = ref(0);

const rankingData = ref([]);

const initChart = () => {
  if (!donutRef.value) return;
  myChart = echarts.init(donutRef.value);

  const option = {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151', fontSize: 12 },
      formatter: '{b}: <strong>{c} 台</strong> ({d}%)'
    },
    series: [
      {
        type: 'pie',
        radius: ['70%', '88%'],
        avoidLabelOverlap: false,
        padAngle: 3,
        itemStyle: {
          borderRadius: 4
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        data: [
          { value: runningCount.value, name: '运行中', itemStyle: { color: '#3b82f6' } },
          { value: shutdownCount.value, name: '已关机', itemStyle: { color: '#d1d5db' } },
          { value: faultyCount.value, name: '故障中', itemStyle: { color: '#ef4444' } }
        ]
      }
    ]
  };

  myChart.setOption(option);
};

const loadData = async () => {
  try {
    const res = await EquipmentLedgerHomeStats();
    if (res.code === 200 && res.data) {
      totalCount.value = res.data.totalCount || 0;
      runningCount.value = res.data.runningCount || 0;
      shutdownCount.value = res.data.shutdownCount || 0;
      faultyCount.value = res.data.faultyCount || 0;
      rankingData.value = res.data.rankingData || [];
      
      if (myChart) {
        myChart.setOption({
          series: [
            {
              data: [
                { value: runningCount.value, name: '运行中', itemStyle: { color: '#3b82f6' } },
                { value: shutdownCount.value, name: '已关机', itemStyle: { color: '#d1d5db' } },
                { value: faultyCount.value, name: '故障中', itemStyle: { color: '#ef4444' } }
              ]
            }
          ]
        });
      }
    }
  } catch (error) {
    console.error("加载首页统计数据失败:", error);
  }
};

const handleResize = () => {
  if (myChart) {
    myChart.resize();
  }
};

onMounted(async () => {
  initChart();
  await loadData();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  if (myChart) {
    myChart.dispose();
  }
});
</script>

<style lang="less" scoped>
.pie-container {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  width: 100%;
}

.status-distribution, .project-ranking {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 0;
  height: 280px; /* 适当增加高度，让间距更高级 */
  box-sizing: border-box;
  // background: #F3F5F9;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.indicator {
  width: 4px;
  height: 16px;
  background-color: #7e5bfd;
  border-radius: 2px;
}

.title-text {
  font-size: 16px;
  font-weight: bold;
  color: #1a1a1a;
  margin: 0;
}

.chart-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 180px;
  padding: 8px 50px;
  // border: 1px solid red;
  background: #F4F5FA;
  border-radius: 8px;
}

.chart-box-outer {
  position: relative;
  width: 180px;
  height: 180px;
}

.chart-box {
  width: 100%;
  height: 100%;
}

.center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.center-num {
  font-size: 32px;
  font-weight: bold;
  color: #1a1a1a;
  line-height: 1;
}

.center-label {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}

.legend-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 160px;
}

.legend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
}

.legend-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;

  &.running { background-color: #3b82f6; }
  &.shutdown { background-color: #d1d5db; }
  &.faulty { background-color: #ef4444; }
}

.label {
  color: #666;
}

.value {
  color: #1a1a1a;
  font-weight: bold;
  font-family: monospace;
}

/* 进度条排行 */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 15px;
  background: #F4F5FA;
  padding: 10px;
  border-radius: 8px;
}

.ranking-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 220px; /* 固定宽度保证对齐 */
}

.rank-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #d1d5db;

  &.rank-1 { background-color: #eab308; } /* 金 */
  &.rank-2 { background-color: #94a3b8; } /* 银 */
  &.rank-3 { background-color: #b45309; } /* 铜 */
}

.project-name {
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.progress-bar-bg {
  background-color: #f3f4f6;
  height: 8px;
  border-radius: 4px;
  flex: 1;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.rate-text {
  font-size: 13px;
  font-weight: 600;
  width: 50px;
  text-align: right;
  font-family: monospace;
}
</style>
